import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkListPage from '../../../components/work/WorkListPage';
import { taskCategoriesApi, taskLabelsApi } from '../../../utils/workApi';
import { buildTaskLabelPayload } from '../../../utils/workPayloadBuilders';
import { workActionsColumn } from '../../../utils/workTableHelpers';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { Button, Table, Modal, Form, Input, message, Tabs, Popconfirm } from 'antd';
import WorkColorPickerField from '../../../components/work/WorkColorPickerField';
import { PlusOutlined, ArrowRightOutlined, DeleteOutlined, AppstoreOutlined } from '@ant-design/icons';
import { TableSkeleton } from '../../../components/SkeletonLoaders';

const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const TaskSettings = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('category');
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [catRes, labelRes] = await Promise.all([
        taskCategoriesApi.getCreateData(),
        taskLabelsApi.getCreateData(),
      ]);
      setCategories(catRes?.categories || []);
      setLabels(labelRes?.labels || []);
    } catch {
      message.error('خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = (type) => {
    setModalType(type);
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      if (modalType === 'category') {
        if (editingId) await taskCategoriesApi.update(editingId, values.category_name);
        else await taskCategoriesApi.create(values.category_name);
      } else {
        const labelBody = buildTaskLabelPayload(values);
        if (editingId) await taskLabelsApi.update(editingId, labelBody);
        else await taskLabelsApi.create(labelBody);
      }
      message.success('ذخیره شد');
      setModalOpen(false);
      load();
    } catch {
      message.error('خطا در ذخیره');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCol = (onDelete) =>
    workActionsColumn((_, row) => (
      <Popconfirm
        title="حذف؟"
        onConfirm={async () => {
          await onDelete(row.id);
          load();
        }}
      >
        <Button type="text" danger icon={<DeleteOutlined />} />
      </Popconfirm>
    ));

  return (
    <WorkListPage
      title="تنظیمات وظایف"
      subtitle="دسته‌بندی و تگ وظایف"
      icon={AppstoreOutlined}
      headerExtra={(
        <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/work/tasks')} style={{ fontFamily: 'CustomFont' }}>
          بازگشت
        </Button>
      )}
      footer={(
        <Modal
          open={modalOpen}
          title={label(modalType === 'category' ? 'دسته وظیفه' : 'تگ وظیفه')}
          onCancel={() => setModalOpen(false)}
          onOk={handleSubmit}
          confirmLoading={submitting}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          destroyOnClose
        >
          <Form form={form} layout="vertical" className="work-panel-form" style={{ fontFamily: 'CustomFont' }}>
            {modalType === 'category' ? (
              <Form.Item name="category_name" label={label('نام دسته')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            ) : null}
            {modalType === 'label' ? (
              <>
                <Form.Item name="label_name" label={label('نام تگ')} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <WorkColorPickerField name="color" label="رنگ" />
                <Form.Item name="description" label={label('توضیح')}>
                  <Input />
                </Form.Item>
              </>
            ) : null}
          </Form>
        </Modal>
      )}
    >
            {loading ? (
              <TableSkeleton rows={5} columns={3} />
            ) : (
              <Tabs
                items={[
                  {
                    key: 'cat',
                    label: label('دسته‌بندی'),
                    children: (
                      <>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          className={`mb-4 ${PANEL_PRIMARY_BTN_CLASS}`}
                          onClick={() => openCreate('category')}
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          دسته جدید
                        </Button>
                        <Table
                          rowKey="id"
                          dataSource={categories}
                          pagination={false}
                          scroll={{ x: true }}
                          columns={[
                            { title: label('نام'), dataIndex: 'category_name' },
                            deleteCol((cid) => taskCategoriesApi.delete(cid)),
                          ]}
                        />
                      </>
                    ),
                  },
                  {
                    key: 'labels',
                    label: label('تگ'),
                    children: (
                      <>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          className={`mb-4 ${PANEL_PRIMARY_BTN_CLASS}`}
                          onClick={() => openCreate('label')}
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          تگ جدید
                        </Button>
                        <Table
                          rowKey="id"
                          dataSource={labels}
                          pagination={false}
                          scroll={{ x: true }}
                          columns={[
                            { title: label('نام'), dataIndex: 'label_name' },
                            { title: label('رنگ'), dataIndex: 'color' },
                            deleteCol((lid) => taskLabelsApi.delete(lid)),
                          ]}
                        />
                      </>
                    ),
                  },
                ]}
              />
            )}
    </WorkListPage>
  );
};

export default TaskSettings;
